/**
 * pdfGenerator.js
 * ---------------
 * Generates the downloadable PDF report using jsPDF.
 *
 * ALL PDF layout, styling and content decisions live in this file.
 * To change the PDF design, edit only this file.
 *
 * DESIGN DECISIONS:
 *   - Page size: A4 (210mm × 297mm)
 *   - Fonts: Helvetica (jsPDF built-in) used for PDF rendering.
 *     Exo 2 and Open Sans cannot be embedded in jsPDF without a custom
 *     font build step. Helvetica Bold approximates the heading weight.
 *     If exact font matching is required in future, see the jsPDF custom
 *     font documentation: https://github.com/parallax/jsPDF#custom-fonts
 *   - Colours: Brand tokens are defined as constants at the top of this file.
 *     If brand colours change, update them here.
 *   - Logo: Loaded as a base64 PNG. The logo is placed on a dark navy header
 *     block so the transparent PNG renders correctly against a coloured surface.
 *
 * LOGO NOTE:
 *   The LOGO_BASE64 constant below must be set to the base64 string of the
 *   Cawood logo PNG. In the built React app, this is handled by importing
 *   the logo file directly (webpack converts it to base64 automatically):
 *     import logoBase64 from "../../assets/CAWOOD-Logo-Light-Cropped.png";
 *   The import is passed into generatePDF() as the logoBase64 parameter.
 *
 * PAGES GENERATED:
 *   Page 1    — Cover: logo, organisation name, date, overall score, intro text
 *   Page 2    — Maturity wheel chart + domain score table
 *   Page 3    — Organisational profile: combined profile name + narrative
 *   Pages 4+  — Per-domain results: level, score, narrative, perception gap note
 *   Final     — Next steps: prompt for full Cawood assessment
 *
 * Connected files:
 *   - ResultsStep.jsx  calls generatePDF() and passes in radarChartCanvas
 *   - scoring.js       provides the results object consumed here
 *   - narratives.js    narrative text is already resolved in the results object
 */

import { jsPDF } from "jspdf";

// ─── Brand colour constants (RGB arrays for jsPDF setTextColor / setFillColor) ─
// Update these if the brand palette changes.
const COLOUR = {
  navy:      [28,  37,  65],   // #1C2541 — dark background
  slate:     [58,  80, 107],   // #3A506B — body text on light bg
  coral:     [255, 111,  97],  // #FF6F61 — primary accent
  coralSoft: [255, 158, 148],  // #FF9E94 — secondary accent
  cta:       [12,  245, 126],  // #0CF57E — highlight green
  light:     [247, 247, 247],  // #F7F7F7 — light background
  white:     [255, 255, 255],
  black:     [0,   0,   0],
};

// ─── Layout constants (millimetres) ──────────────────────────────────────────
const PAGE = { w: 210, h: 297 };
const MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const CONTENT_W = PAGE.w - MARGIN.left - MARGIN.right; // 170mm usable width

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Sets the current draw colour (fill) from an RGB array.
 * @param {jsPDF} doc
 * @param {number[]} rgb
 */
function setFill(doc, rgb) {
  doc.setFillColor(...rgb);
}

/**
 * Sets the current text colour from an RGB array.
 * @param {jsPDF} doc
 * @param {number[]} rgb
 */
function setTextColour(doc, rgb) {
  doc.setTextColor(...rgb);
}

/**
 * Draws a filled rectangle spanning the full page width.
 * Used for header and footer bands.
 * @param {jsPDF} doc
 * @param {number} y       - Top of the band in mm
 * @param {number} height  - Height of the band in mm
 * @param {number[]} rgb   - Fill colour
 */
function drawFullWidthBand(doc, y, height, rgb) {
  setFill(doc, rgb);
  doc.rect(0, y, PAGE.w, height, "F");
}

/**
 * Adds a page footer with the Cawood URL and page number.
 * Called at the end of each page before doc.addPage().
 * @param {jsPDF} doc
 * @param {number} pageNum
 * @param {number} totalPages - Pass 0 during generation; update in post-processing if needed.
 */
function addFooter(doc, pageNum) {
  drawFullWidthBand(doc, PAGE.h - 12, 12, COLOUR.navy);
  doc.setFontSize(8);
  setTextColour(doc, COLOUR.light);
  doc.setFont("helvetica", "normal");
  doc.text("cawood.io", MARGIN.left, PAGE.h - 4.5);
  doc.text(
    `Digital Maturity Assessment — Page ${pageNum}`,
    PAGE.w / 2,
    PAGE.h - 4.5,
    { align: "center" }
  );
  doc.text(
    new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    PAGE.w - MARGIN.right,
    PAGE.h - 4.5,
    { align: "right" }
  );
}

/**
 * Wraps and renders a block of body text with automatic line breaks.
 * Returns the Y position after the last line of text.
 * @param {jsPDF} doc
 * @param {string} text
 * @param {number} x        - Left edge in mm
 * @param {number} y        - Top of text block in mm
 * @param {number} maxWidth - Maximum line width in mm
 * @param {number} lineH    - Line height in mm (typically 6–7)
 * @returns {number} Y position after final line
 */
function writeWrappedText(doc, text, x, y, maxWidth, lineH = 6) {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineH;
}

/**
 * Draws a thin horizontal rule.
 * @param {jsPDF} doc
 * @param {number} y
 * @param {number[]} rgb
 */
function drawRule(doc, y, rgb = COLOUR.light) {
  doc.setDrawColor(...rgb);
  doc.setLineWidth(0.3);
  doc.line(MARGIN.left, y, PAGE.w - MARGIN.right, y);
}

// ─── Main export: generatePDF ─────────────────────────────────────────────────

/**
 * generatePDF()
 * -------------
 * Generates the full assessment PDF and returns it as a base64 string.
 *
 * @param {Object} params
 * @param {Object} params.results        - Full results object from calculateResults()
 * @param {string} params.logoBase64     - Logo PNG as base64 string (from webpack import)
 * @param {string} params.radarChartImg  - Radar chart as base64 PNG (from canvas.toDataURL())
 *
 * @returns {Object} { base64: string, filename: string }
 *   base64   — the PDF as a base64 string, for sending to the Cloudflare Worker
 *   filename — suggested download filename
 */
export function generatePDF({ results, logoBase64, radarChartImg }) {

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const { respondent, domains, averageScore, focusDomainId, radarData } = results;

  let pageNum = 1;

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 1 — Cover page
  // ════════════════════════════════════════════════════════════════════════════

  // Header band — dark navy
  drawFullWidthBand(doc, 0, 55, COLOUR.navy);

  // Logo — placed on the dark header so transparency renders cleanly
  // logoBase64 is the raw base64 string of the PNG (without the data: prefix).
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, "PNG", MARGIN.left, 12, 60, 16);
    } catch (e) {
      // If logo fails to load, continue without it — don't break PDF generation
      console.warn("PDF: logo could not be added.", e);
    }
  }

  // Assessment title on header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setTextColour(doc, COLOUR.coralSoft);
  doc.text("Digital Maturity Assessment", PAGE.w - MARGIN.right, 20, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setTextColour(doc, COLOUR.light);
  doc.text("Lightweight Report — Version 1", PAGE.w - MARGIN.right, 27, { align: "right" });

  // Organisation name — large, below header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setTextColour(doc, COLOUR.navy);
  doc.text(respondent.organisation || "Your Organisation", MARGIN.left, 75);

  // Respondent name and date
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  setTextColour(doc, COLOUR.slate);
  doc.text(respondent.fullName, MARGIN.left, 85);
  doc.text(
    new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    MARGIN.left,
    92
  );

  drawRule(doc, 98);

  // Overall score panel
  doc.setFont("helvetica", "bold");
  doc.setFontSize(38);
  setTextColour(doc, COLOUR.coral);
  doc.text(`${averageScore}/5`, MARGIN.left, 120);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  setTextColour(doc, COLOUR.slate);
  doc.text("Overall digital maturity score", MARGIN.left, 128);
  doc.text(`Sector: ${respondent.sector}   ·   Headcount: ${respondent.headcount}`, MARGIN.left, 135);

  drawRule(doc, 141);

  // Intro paragraph
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  setTextColour(doc, COLOUR.navy);
  doc.text("About this report", MARGIN.left, 151);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setTextColour(doc, COLOUR.slate);
  const introText =
    `This report summarises the results of your Cawood.io Digital Maturity Assessment. ` +
    `It covers six domains: Digital Strategy, Data & Governance, People & Skills, ` +
    `Technology & Architecture, Service Management, and AI & Machine Learning. ` +
    `Each domain is scored on a five-point scale from Unaware (1) to Integrated (5). ` +
    `\n\nThe scores reflect your own assessment of your organisation's current position. ` +
    `They are a starting point for a conversation — not a final verdict. ` +
    `The perception gap scores, where you rated how your broader organisation compares to your team, ` +
    `are often the most valuable part of that conversation.`;

  writeWrappedText(doc, introText, MARGIN.left, 160, CONTENT_W, 5.5);

  // Focus domain callout
  const focusDomain = domains.find((d) => d.id === focusDomainId);
  if (focusDomain) {
    setFill(doc, COLOUR.light);
    doc.roundedRect(MARGIN.left, 218, CONTENT_W, 22, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    setTextColour(doc, COLOUR.coral);
    doc.text("YOUR SELECTED FOCUS AREA", MARGIN.left + 5, 226);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    setTextColour(doc, COLOUR.navy);
    doc.text(focusDomain.label, MARGIN.left + 5, 234);
  }

  // Next steps teaser at bottom of cover
  drawRule(doc, 248);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setTextColour(doc, COLOUR.slate);
  doc.text(
    "This lightweight assessment surfaces patterns. For root causes and a detailed roadmap, see the full Cawood assessment — details on the final page.",
    MARGIN.left, 256, { maxWidth: CONTENT_W }
  );

  addFooter(doc, pageNum);

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 2 — Radar chart
  // ════════════════════════════════════════════════════════════════════════════

  doc.addPage();
  pageNum++;

  drawFullWidthBand(doc, 0, 18, COLOUR.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  setTextColour(doc, COLOUR.light);
  doc.text("Your maturity profile", MARGIN.left, 12);

  // Radar chart image — captured from the canvas rendered in ResultsStep
  if (radarChartImg) {
    try {
      // Centre the chart on the page, leaving room for the score table below
      const chartSize = 140;
      const chartX    = (PAGE.w - chartSize) / 2;
      doc.addImage(radarChartImg, "PNG", chartX, 25, chartSize, chartSize);
    } catch (e) {
      console.warn("PDF: radar chart image could not be added.", e);
    }
  }

  // Domain score summary table below the chart
  let tableY = 175;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColour(doc, COLOUR.slate);
  doc.text("Domain", MARGIN.left, tableY);
  doc.text("Level", 110, tableY);
  doc.text("Score", 155, tableY);
  drawRule(doc, tableY + 3, COLOUR.slate);
  tableY += 9;

  domains.forEach((d) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    // Colour dot to match radar chart
    setFill(doc, hexToRgb(d.color));
    doc.circle(MARGIN.left + 2, tableY - 1.5, 2, "F");

    setTextColour(doc, COLOUR.slate);
    doc.text(d.label, MARGIN.left + 7, tableY);
    doc.text(d.levelLabel, 110, tableY);

    setTextColour(doc, COLOUR.coral);
    doc.setFont("helvetica", "bold");
    doc.text(`${d.displayScore}/5`, 155, tableY);

    // Perception gap indicator
    if (d.perceptionFlagged) {
      doc.setFont("helvetica", "normal");
      setTextColour(doc, COLOUR.coralSoft);
      doc.setFontSize(8);
      const gapLabel = d.perceptionDelta > 0 ? "+ perception gap" : "- perception gap";
      doc.text(gapLabel, 170, tableY);
    }

    drawRule(doc, tableY + 3, COLOUR.light);
    tableY += 9;
  });

  addFooter(doc, pageNum);
  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 3 — Organisational profile
  // ════════════════════════════════════════════════════════════════════════════
  // Renders the combined profile: profile name, weak domain pair narrative,
  // and strength anchor paragraph. Assembled by buildCombinedProfile() in
  // scoring.js from WEAK_PAIRS and STRENGTH_ANCHORS in narratives.js.

  const { combinedProfile } = results;

  if (combinedProfile) {
    doc.addPage();
    pageNum++;

    // ── Header band ──────────────────────────────────────────────────────────
    drawFullWidthBand(doc, 0, 18, COLOUR.navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    setTextColour(doc, COLOUR.light);
    doc.text("Your organisational profile", MARGIN.left, 12);

    // ── Profile name ─────────────────────────────────────────────────────────
    // Large display treatment — the profile name is the headline of this page.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    setTextColour(doc, COLOUR.navy);
    doc.text(combinedProfile.profileName, MARGIN.left, 36);

    // ── Weak domain pair labels ───────────────────────────────────────────────
    // Two pill badges side by side showing the two lowest-scoring domains.
    const pillY = 44;
    const pillH = 8;
    const pillPadX = 5;

    // Pill 1 — weakest domain
    const pill1Text = combinedProfile.weakDomain1.label;
    const pill1W    = doc.getTextWidth(pill1Text) + pillPadX * 2;
    setFill(doc, COLOUR.coral);
    doc.roundedRect(MARGIN.left, pillY, pill1W, pillH, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    setTextColour(doc, COLOUR.white);
    doc.text(pill1Text, MARGIN.left + pillPadX, pillY + 5.5);

    // Pill 2 — second weakest domain
    const pill2Text = combinedProfile.weakDomain2.label;
    const pill2X    = MARGIN.left + pill1W + 4;
    const pill2W    = doc.getTextWidth(pill2Text) + pillPadX * 2;
    setFill(doc, COLOUR.coralSoft);
    doc.roundedRect(pill2X, pillY, pill2W, pillH, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    setTextColour(doc, COLOUR.white);
    doc.text(pill2Text, pill2X + pillPadX, pillY + 5.5);

    // Strength label — right-aligned, same row
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setTextColour(doc, COLOUR.slate);
    doc.text(
      `Strength: ${combinedProfile.strongDomain.label}`,
      PAGE.w - MARGIN.right,
      pillY + 5.5,
      { align: "right" }
    );

    drawRule(doc, pillY + pillH + 6);

    // ── What this means heading ───────────────────────────────────────────────
    let yPos = pillY + pillH + 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.navy);
    doc.text("What this means for your organisation", MARGIN.left, yPos);
    yPos += 8;

    // ── Combined narrative ────────────────────────────────────────────────────
    // The narrative is two paragraphs joined by \n\n — split and render each.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.slate);

    const paragraphs = combinedProfile.narrative.split("\n\n");
    for (const para of paragraphs) {
      yPos = writeWrappedText(doc, para.trim(), MARGIN.left, yPos, CONTENT_W, 5.5);
      yPos += 6; // paragraph gap
    }

    // ── Score summary strip ───────────────────────────────────────────────────
    // Three scores in a row: weak1 · weak2 · strong — compact reference.
    yPos += 4;
    drawRule(doc, yPos);
    yPos += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    setTextColour(doc, COLOUR.navy);
    doc.text("Profile scores at a glance", MARGIN.left, yPos);
    yPos += 7;

    const scoreItems = [
      { label: combinedProfile.weakDomain1.label,  score: combinedProfile.weakDomain1.score,  colour: COLOUR.coral,     role: "PRIMARY GAP"   },
      { label: combinedProfile.weakDomain2.label,  score: combinedProfile.weakDomain2.score,  colour: COLOUR.coralSoft, role: "SECONDARY GAP" },
      { label: combinedProfile.strongDomain.label, score: combinedProfile.strongDomain.score, colour: COLOUR.cta,       role: "STRENGTH"       },
    ];

    const colW = CONTENT_W / 3;
    scoreItems.forEach((item, idx) => {
      const x = MARGIN.left + idx * colW;

      // Score value — large and coloured
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      setTextColour(doc, item.colour);
      doc.text(`${item.score}/5`, x, yPos + 8);

      // Domain label below score
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      setTextColour(doc, COLOUR.slate);
      const labelLines = doc.splitTextToSize(item.label, colW - 4);
      doc.text(labelLines, x, yPos + 14);

      // Role label
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      setTextColour(doc, COLOUR.slate);
      doc.text(item.role, x, yPos + 20);
    });

    addFooter(doc, pageNum);
  }


  // ════════════════════════════════════════════════════════════════════════════
  // PAGES 4+ — Per-domain results
  // ════════════════════════════════════════════════════════════════════════════

  // Sort so the focus domain appears first
  const sortedDomains = [
    ...domains.filter((d) => d.isFocusDomain),
    ...domains.filter((d) => !d.isFocusDomain),
  ];

  sortedDomains.forEach((domain) => {
    doc.addPage();
    pageNum++;

    // Domain header band — coral accent
    drawFullWidthBand(doc, 0, 18, COLOUR.navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    setTextColour(doc, COLOUR.light);
    doc.text(domain.label, MARGIN.left, 12);

    // Focus domain badge
    if (domain.isFocusDomain) {
      setFill(doc, COLOUR.coral);
      doc.roundedRect(PAGE.w - MARGIN.right - 42, 5, 40, 8, 2, 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      setTextColour(doc, COLOUR.white);
      doc.text("YOUR FOCUS AREA", PAGE.w - MARGIN.right - 22, 10.5, { align: "center" });
    }

    // Level and score
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    setTextColour(doc, COLOUR.coral);
    doc.text(domain.levelLabel, MARGIN.left, 38);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    setTextColour(doc, COLOUR.slate);
    doc.text(`Score: ${domain.displayScore} / 5`, MARGIN.left, 47);

    drawRule(doc, 52);

    // Narrative
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.navy);
    doc.text("What this means for your organisation", MARGIN.left, 62);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.slate);
    let yPos = writeWrappedText(doc, domain.narrative, MARGIN.left, 71, CONTENT_W, 5.5);

    yPos += 8;
    drawRule(doc, yPos);
    yPos += 8;

    // Perception gap section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.navy);
    doc.text("Perception gap", MARGIN.left, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    setTextColour(doc, COLOUR.slate);

    if (domain.perceptionFlagged) {
      const gapDirection = domain.perceptionDelta > 0
        ? "You rated your team higher than you believe the broader organisation would score this area."
        : "You rated your team lower than you believe the broader organisation would score this area.";

      const gapImplication = domain.perceptionDelta > 0
        ? "This is worth exploring. It may indicate that good practice in your team isn't yet visible or adopted organisation-wide — or that your team has higher standards than the rest of the organisation recognises."
        : "This could indicate that other parts of the organisation are ahead of where your team currently sits — or that your team is applying a more critical lens. Either way, it's a useful conversation to have.";

      yPos = writeWrappedText(doc, gapDirection, MARGIN.left, yPos, CONTENT_W, 5.5);
      yPos += 4;
      yPos = writeWrappedText(doc, gapImplication, MARGIN.left, yPos, CONTENT_W, 5.5);
    } else {
      yPos = writeWrappedText(
        doc,
        "Your perception of your team's performance and the broader organisation's performance are broadly aligned in this area.",
        MARGIN.left, yPos, CONTENT_W, 5.5
      );
    }

    addFooter(doc, pageNum);
  });

  // ════════════════════════════════════════════════════════════════════════════
  // FINAL PAGE — Next steps
  // ════════════════════════════════════════════════════════════════════════════

  doc.addPage();
  pageNum++;

  drawFullWidthBand(doc, 0, 18, COLOUR.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  setTextColour(doc, COLOUR.light);
  doc.text("What happens next", MARGIN.left, 12);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  setTextColour(doc, COLOUR.navy);
  doc.text("This assessment surfaces patterns.", MARGIN.left, 35);
  doc.text("The full Cawood assessment finds root causes.", MARGIN.left, 44);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setTextColour(doc, COLOUR.slate);

  const nextStepsText =
    `The lightweight assessment you've just completed is designed to give you a clear picture ` +
    `of where your organisation sits across six digital domains. It's honest, and it's a ` +
    `useful starting point — but it isn't diagnostic. It tells you what the pattern looks like. ` +
    `It doesn't tell you why it looks that way, or what specifically to do about it.\n\n` +
    `The full Cawood Digital Maturity Assessment does. It combines this self-assessment with ` +
    `a structured conversation, document review and stakeholder input to produce a calibrated ` +
    `picture of your organisation's position — and a clear, prioritised roadmap for what ` +
    `to do next.`;

  let yPos = writeWrappedText(doc, nextStepsText, MARGIN.left, 58, CONTENT_W, 5.5);

  yPos += 10;

  // CTA panel
  setFill(doc, COLOUR.navy);
  doc.roundedRect(MARGIN.left, yPos, CONTENT_W, 30, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  setTextColour(doc, COLOUR.cta);
  doc.text("Talk to us about a full assessment", MARGIN.left + 8, yPos + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setTextColour(doc, COLOUR.light);
  doc.text("cawood.io  ·  hello@cawood.io", MARGIN.left + 8, yPos + 21);

  addFooter(doc, pageNum);

  // ── Return base64 and filename ─────────────────────────────────────────────
  const base64 = doc.output("datauristring").split(",")[1];

  const safeOrg = respondent.organisation
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const dateStamp = new Date().toISOString().split("T")[0];
  const filename  = `cawood-assessment-${safeOrg}-${dateStamp}.pdf`;

  return { base64, filename };
}

// ─── Utility: hex to RGB array ────────────────────────────────────────────────

/**
 * Converts a hex colour string to an RGB array for jsPDF.
 * @param {string} hex - e.g. "#FF6F61"
 * @returns {number[]} e.g. [255, 111, 97]
 */
function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16),
  ];
}
